import { UserServiceClient } from 'fitbeat.api-clients';
import { musicPlatform } from 'fitbeat.gym-core/config';
import { CardioOrResistance } from 'fitbeat.models';
import { NativeEventEmitter, NativeModules } from 'react-native';
import { getApiConfig } from '../../../config';
import { analyicsForAction } from '../../../services/google-analytics/analyicsForAction';
import { closeSetupScreen } from '../../actions';
import {
  CONNECTION_STATE_CHANGE,
  FETCH_FB_PLAYLISTS_SUCCESS,
  FETCH_PLAYLISTS_SUCCESS,
  FETCH_RECOMMENDED_PLAYLISTS_SUCCESS,
  FETCH_SONGS_SUCCESS,
  FETCH_USER_PLAYLISTS_SUCCESS,
  FETCH_USER_PROFILE_SUCCESS,
  fetchMusicDataFromAsyncStorage,
  SELECTED_PLAYLIST,
} from './actions';
import { IFBPlaylists, IMusicPlayerSource, IPlayerState, IPlaylist, ISong } from './interfaces';
import { getPlaylistsSelector } from './selectors';
import { DeviceSetupScreens } from '../../utils/DeviceSetupScreens';

const { AppleMusicAPI } = NativeModules;

export class AppleMusicSource extends IMusicPlayerSource {
  private userToken?: string;
  private developerToken?: string;
  private fbPlaylists?: IFBPlaylists;
  private readonly userServiceClient: UserServiceClient;
  private readonly nativeEventEmitter: NativeEventEmitter;

  constructor(dispatch, getAppState, updatePlayerStateCallback) {
    super(dispatch, getAppState, updatePlayerStateCallback);
    this.userServiceClient = new UserServiceClient(getApiConfig(), fetch, null, undefined);
    this.nativeEventEmitter = new NativeEventEmitter(AppleMusicAPI);
  }

  public async clean(): Promise<void> {
    if (this.isLoggedIn()) {
      await AppleMusicAPI.stopMusicAndCleanUpEvents();
    }
    this.nativeEventEmitter.removeAllListeners('PlaybackStateUpdate');
  }

  public async initialize(): Promise<void> {
    const res = await this.userServiceClient.getAppleDeveloperToken();
    this.developerToken = res.developerToken;
    await AppleMusicAPI.initialize(res.developerToken);
    this.nativeEventEmitter.addListener(
      'PlaybackStateUpdate',
      this.playbackStateUpdated,
    );
    await this.login();
    this.dispatch({ type: FETCH_USER_PROFILE_SUCCESS, payload: { product: 'premium' } });

    await Promise.all([
      this.loadUserPlaylists(),
      this.loadRecommendedPlaylists(),
      this.loadFitbeatPlaylists(),
    ]);

    const { homeWorkouts } = this.getAppState();
    const initialPlaylist = await this.getInitialPlaylistToPlay(homeWorkouts.homeWorkoutSession);

    await this.setInitialShuffle();

    const playerState = await this.playPlaylist(initialPlaylist, true);

    this.dispatch(closeSetupScreen(DeviceSetupScreens.Music));

    this.dispatch({
      type: CONNECTION_STATE_CHANGE,
      payload: { isConnected: true, playerState },
      ...analyicsForAction('workout_music_players_auth_success'),
    });

    await this.loadPlaylistSongs(initialPlaylist);

    this.dispatch({
      type: FETCH_PLAYLISTS_SUCCESS,
      ...analyicsForAction('workout_music_playlists_loaded'),
    });
  }

  public async playPlaylist(playlist: IPlaylist, calledDuringInitialize = false): Promise<IPlayerState> {
    const playerState = await AppleMusicAPI.selectPlaylistAndPlay(playlist.id);
    this.dispatch({
      type: SELECTED_PLAYLIST,
      payload: { playlist },
      ...analyicsForAction('workout_music_playlists_select'),
    });
    if (!calledDuringInitialize) {
      await this.loadPlaylistSongs(playlist);
    }
    return this.mapApplePlayerStateToIPlayerState(playerState);
  }

  public playSong(song: ISong): Promise<void> {
    return Promise.resolve(undefined);
  }

  public setShuffling(shuffling: boolean): Promise<void> {
    if (shuffling) {
      return AppleMusicAPI.enableShuffling();
    }
    return AppleMusicAPI.disableShuffling();
  }

  public async login() {
    const { userToken } = await AppleMusicAPI.loginAndReturnUserToken();
    this.userToken = userToken;
  }

  public skipNext(): Promise<void> {
    return AppleMusicAPI.skipNext();
  }

  public skipPrevious(): Promise<void> {
    return AppleMusicAPI.skipPrevious();
  }

  public pause(): Promise<void> {
    return AppleMusicAPI.pause();
  }

  public play(): Promise<void> {
    return AppleMusicAPI.resume();
  }

  public async getPlaybackState(): Promise<IPlayerState> {
    return this.mapApplePlayerStateToIPlayerState(await AppleMusicAPI.getPlaybackState());
  }

  public async loadUserPlaylists(): Promise<void> {
    const data = await AppleMusicAPI.getUserPlaylists();
    const mappedPlaylists = data.map((playlist) => {
      playlist.id = playlist.id.toString();
      playlist.uri = playlist.uri.toString();
      return playlist;
    });

    if (mappedPlaylists.length) {
      this.dispatch({
        type: FETCH_USER_PLAYLISTS_SUCCESS,
        payload: mappedPlaylists,
      });
    }
  }

  public async getPlaylistFromId(playlistId: string): Promise<IPlaylist> {
    this.guardNotLoggedIn();
    const result = await fetch(`https://api.music.apple.com/v1/catalog/au/playlists/${playlistId}`,
      this.getFetchOptions());
    if (result.status !== 200) {
      throw new Error(`Unable to get playlist for id ${playlistId} failed with status ${result.status} and msg "${result.statusText}"`);
    }

    const parsedResponse = await result.json();
    return this.mapAppleMusicItemToIPlaylist(parsedResponse.data[0]);
  }

  public async searchPlaylists(term: string): Promise<IPlaylist[]> {
    this.guardNotLoggedIn();
    const result = await fetch(`https://api.music.apple.com/v1/catalog/au/search?term=${term}&types=playlists`,
      this.getFetchOptions());
    if (result.status !== 200) {
      throw new Error(result.statusText);
    }

    const data = await result.json();
    return this.mapResponseToPlaylists(data.results.playlists.data);
  }

  public async loadRecommendedPlaylists(): Promise<void> {
    const recommendedPlaylists = await this.searchPlaylists('fitness');
    if (recommendedPlaylists.length) {
      this.dispatch({
        type: FETCH_RECOMMENDED_PLAYLISTS_SUCCESS,
        payload: recommendedPlaylists,
      });
    }
  }

  private async loadPlaylistSongs(playlist: IPlaylist): Promise<void> {
    this.guardNotLoggedIn();
    if (playlist.id.includes('pl.')) {
      const result = await fetch(`https://api.music.apple.com/v1/catalog/au/playlists/${playlist.id}`,
        this.getFetchOptions());
      if (result.status !== 200) {
        throw new Error(result.statusText);
      }
      const { data } = await result.json();
      const songs = this.mapApplePlaylistToISongs(data[0].relationships.tracks.data);
      this.dispatch({
        type: FETCH_SONGS_SUCCESS,
        payload: songs,
      });
    } else {
      const tracks = await AppleMusicAPI.getUserPlaylistTracks(playlist.id);
      this.dispatch({
        type: FETCH_SONGS_SUCCESS,
        payload: tracks,
      });
    }
  }

  private guardNotLoggedIn() {
    if (!this.isLoggedIn()) {
      throw new Error('you need to log in first');
    }
  }

  private getPlaylistIdFromPlaylistUrl = (url: string) => {
    const id = url.split('/').pop();
    return id || '';
  }

  private async loadFitbeatPlaylists(): Promise<void> {
    const fbPlaylists = await this.userServiceClient.getUserPlatformLink(musicPlatform.AppleMusic);
    const buildId = this.getPlaylistIdFromPlaylistUrl(fbPlaylists.buildPlaylist);
    const burnId = this.getPlaylistIdFromPlaylistUrl(fbPlaylists.burnPlaylist);
    const balanceId = this.getPlaylistIdFromPlaylistUrl(fbPlaylists.balancePlaylist);

    const [fbBuildPlaylist, fbBurnPlaylist, fbBalancePlaylist, ...playlists] = await Promise.all([
      this.getPlaylistFromId(buildId),
      this.getPlaylistFromId(burnId),
      this.getPlaylistFromId(balanceId),
      ...fbPlaylists.playlists.map((playlist) =>
        this.getPlaylistFromId(this.getPlaylistIdFromPlaylistUrl(playlist.link))),
    ]);

    this.fbPlaylists = {
      build: fbBuildPlaylist,
      burn: fbBurnPlaylist,
      balance: fbBalancePlaylist,
    };

    this.dispatch({
      type: FETCH_FB_PLAYLISTS_SUCCESS,
      payload: {
        playlists,
        defaultBuildBurnPlaylistIds: [buildId, burnId, balanceId],
      },
    });
  }

  private async setInitialShuffle() {
    const playerState: IPlayerState = await AppleMusicAPI.getPlaybackState();
    if (!playerState.isShuffling) {
      await AppleMusicAPI.enableShuffling();
    }
  }

  private async getInitialPlaylistToPlay(homeWorkoutSession: any): Promise<IPlaylist> {
    const storedData = await fetchMusicDataFromAsyncStorage();
    const playlists = getPlaylistsSelector(this.getAppState());
    if (storedData && storedData.musicPLayer === musicPlatform.AppleMusic && storedData.playlistUri) {
      const previousPlaylist = playlists.find((playlist: IPlaylist) => playlist.uri === storedData.playlistUri);
      if (previousPlaylist) {
        return previousPlaylist;
      }
    }
    const isBurnWorkout = homeWorkoutSession.workoutSettings.cardioOrResistance === CardioOrResistance.Cardio;
    const isBuildWorkout = homeWorkoutSession.workoutSettings.cardioOrResistance === CardioOrResistance.Resistance;
    return isBurnWorkout ? this.fbPlaylists!.burn
    : isBuildWorkout ? this.fbPlaylists!.build :
    this.fbPlaylists!.balance;
  }

  private mapResponseToPlaylists(data: any): IPlaylist[] {
    if (!data) {
      return [];
    }
    return data.map((item: any) => this.mapAppleMusicItemToIPlaylist(item));
  }

  private mapAppleMusicItemToIPlaylist(item): IPlaylist {
    return {
      id: item.id,
      title: item.attributes.name,
      image: this.getImageUrl(item),
      uri: item.href,
    };
  }

  private getImageUrl(item: any) {
    if (!item.attributes.artwork) {
      return ';';
    }
    return item.attributes.artwork.url.replace('{w}x{h}', '300x300');
  }

  private getThumbnailUrl(item: any) {
    if (!item.attributes.artwork) {
      return ';';
    }
    return item.attributes.artwork.url.replace('{w}x{h}', '60x60');
  }

  private getFetchOptions(): any {
    return {
      headers: {
        'Authorization': `Bearer ${this.developerToken}`,
        'Music-User-Token': this.userToken,
      },
      method: 'GET',
      handle401: false,
    };
  }

  private mapApplePlayerStateToIPlayerState(applePlayerState): IPlayerState {
    return {
      ...applePlayerState,
      canSkipNext: true,
      canSkipPrevious: true,
      canToggleShuffle: true,
    };
  }

  private mapApplePlaylistToISongs(appleSongs): ISong[] {
    if (!appleSongs) {
      return [];
    }
    return appleSongs.map((item) => ({
      id: item.id,
      uri: item.attributes.url,
      title: item.attributes.name,
      artist: item.attributes.artistName,
      duration: item.attributes.durationInMillis,
      image: this.getImageUrl(item),
      thumbnail: this.getThumbnailUrl(item),
    }));
  }

  private isLoggedIn() {
    return this.userToken && this.developerToken;
  }

  private playbackStateUpdated = (playbackState) => {
    const playerState = this.mapApplePlayerStateToIPlayerState(playbackState);
    this.updatePlayerStateCallback(playerState);
  }
}
